import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { FOLD_COUNT, FoldToggle } from '@/features/projects/fold-toggle'
import { formatEventTime, parseUTCDate } from '@/lib/utils'
import type { components } from '@/api/schema'

type CustomerThread = components['schemas']['ProjectCustomerCommunicationThreadRead']

const ATTENTION = new Set(['needs_attention', 'error'])

function ThreadRow({ thread }: { thread: CustomerThread }) {
  const { digest } = thread
  return (
    <li>
      <Link
        to="/customer-comms/$threadId"
        params={{ threadId: String(thread.id) }}
        className="-mx-2 block rounded-md px-2 py-1.5 transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
        data-testid={`project-customer-thread-${thread.id}`}
      >
        <span className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate text-sm font-medium">{thread.subject}</span>
          <time
            dateTime={thread.last_activity_at}
            className="shrink-0 text-xs tabular-nums text-muted-foreground"
          >
            {formatEventTime(thread.last_activity_at)}
          </time>
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span>
            {digest.message_count} message{digest.message_count === 1 ? '' : 's'}
          </span>
          {ATTENTION.has(thread.status) && (
            <span className="font-medium text-destructive">
              {thread.status === 'error' ? 'Error' : 'Needs attention'}
            </span>
          )}
          {digest.open_action_count > 0 && (
            <span className="inline-flex items-center gap-1 font-medium text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {digest.open_action_count} open action{digest.open_action_count === 1 ? '' : 's'}
            </span>
          )}
          {digest.analysis_active && (
            <span className="inline-flex items-center gap-1 font-medium text-violet-700 dark:text-violet-300">
              <Loader2 className="h-3 w-3 motion-safe:animate-spin" aria-hidden />
              Analyzing
            </span>
          )}
        </span>
      </Link>
    </li>
  )
}

/** The customer's email threads for this project, newest first, each opening its thread. */
export function ProjectCustomerThreads({
  threads,
  isLoading,
}: {
  threads: CustomerThread[] | undefined
  isLoading: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  if (isLoading) {
    return (
      <div className="space-y-2 border-t pt-3">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    )
  }
  if (!threads?.length) return null

  const sorted = [...threads].sort(
    (a, b) => parseUTCDate(b.last_activity_at).getTime() - parseUTCDate(a.last_activity_at).getTime(),
  )
  const shown = expanded ? sorted : sorted.slice(0, FOLD_COUNT)

  return (
    <section aria-labelledby="project-customer-threads-title" className="space-y-1 border-t pt-3">
      <h4 id="project-customer-threads-title" className="text-xs font-medium text-muted-foreground">
        Conversations
      </h4>
      <ul id="project-customer-threads" className="space-y-0.5">
        {shown.map((thread) => (
          <ThreadRow key={thread.id} thread={thread} />
        ))}
      </ul>
      {sorted.length > FOLD_COUNT && (
        <FoldToggle
          expanded={expanded}
          total={sorted.length}
          noun="conversations"
          controls="project-customer-threads"
          onToggle={() => setExpanded((value) => !value)}
        />
      )}
    </section>
  )
}
